<template>
  <div class="card mb-3">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h5 class="mb-0">Contract and documents</h5>
      <span v-if="loading" class="text-muted small">Loading…</span>
    </div>
    <div class="card-body">
      <div
        v-if="mode === 'self' && summary && !summary.employee_complete"
        class="alert alert-warning d-flex align-items-start gap-2 mb-3"
        role="status"
      >
        <i class="ti ti-alert-circle fs-20 mt-1"></i>
        <div>
          <strong>Complete your onboarding</strong>
          <div class="small mt-1">
            <span v-if="summary.contract_status === 'waiting'">Sign your employment contract.</span>
            <span v-if="summary.documents_pending > 0">
              <span v-if="summary.contract_status === 'waiting'"> </span>
              Upload {{ summary.documents_pending }} requested document(s).
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="summary && (mode === 'hr' ? !summary.hr_complete : !summary.employee_complete)"
        class="border rounded p-3 mb-3 bg-light"
      >
        <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
          <h6 class="mb-0 fw-semibold">Onboarding checklist</h6>
          <span
            class="badge"
            :class="mode === 'hr'
              ? (summary.hr_complete ? 'bg-success' : 'bg-warning text-dark')
              : (summary.employee_complete ? 'bg-success' : 'bg-warning text-dark')"
          >
            {{ mode === "hr"
              ? (summary.hr_complete ? "Complete" : `${summary.hr_tasks_pending} left`)
              : (summary.employee_complete ? "Complete" : `${summary.employee_tasks_pending} left`) }}
          </span>
        </div>
        <ul class="list-unstyled mb-0 dq-onboarding-checklist">
          <li
            v-for="item in visibleChecklist"
            :key="item.key"
            class="d-flex align-items-start gap-2 py-1"
          >
            <i
              class="ti mt-1"
              :class="item.done ? 'ti-circle-check-filled text-success' : 'ti-circle text-muted'"
            ></i>
            <div>
              <span :class="{ 'text-muted text-decoration-line-through': item.done }">{{ item.label }}</span>
              <div v-if="!item.done" class="small text-muted">{{ item.hint }}</div>
            </div>
          </li>
        </ul>
      </div>

      <p class="text-muted mb-3">
        <template v-if="mode === 'hr'">
          Upload the contract for this employee, and keep ID and statutory copies on the same file. Mark a copy as missing when the employee should upload it after they sign in.
        </template>
        <template v-else>
          Sign the contract your employer attached, and upload any copies they have asked for.
        </template>
      </p>

      <div v-if="mode === 'hr'" class="border rounded p-3 mb-3">
        <h6 class="mb-2">Add a contract</h6>
        <div class="row g-2 align-items-end">
          <div class="col-md-8">
            <label class="form-label">Contract PDF</label>
            <input ref="contractInput" class="form-control" type="file" accept="application/pdf,.pdf" @change="onContractPicked" />
          </div>
          <div class="col-md-4">
            <button class="btn btn-primary w-100" type="button" :disabled="saving || !contractFile" @click="uploadContract">
              Upload contract
            </button>
          </div>
        </div>
      </div>

      <div v-if="mode === 'hr'" class="border rounded p-3 mb-3">
        <h6 class="mb-2">Add a supporting document</h6>
        <div class="row g-2">
          <div class="col-md-4">
            <label class="form-label">Type</label>
            <select v-model="supporting.document_type" class="form-select">
              <option v-for="option in supportingTypes" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <div v-if="supporting.document_type === 'other'" class="col-md-4">
            <label class="form-label">Name</label>
            <input v-model="supporting.title" class="form-control" type="text" maxlength="255" />
          </div>
          <div class="col-md-4">
            <label class="form-label">File, if you already have it</label>
            <input ref="supportingInput" class="form-control" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" @change="onSupportingPicked" />
          </div>
        </div>
        <div class="d-flex flex-wrap gap-2 mt-3">
          <button class="btn btn-primary" type="button" :disabled="saving || !supportingFile" @click="saveSupporting(true)">
            Save file
          </button>
          <button class="btn btn-outline-primary" type="button" :disabled="saving" @click="saveSupporting(false)">
            Ask the employee to upload
          </button>
        </div>
      </div>

      <h6 class="mb-2">Contracts</h6>
      <div v-if="!contracts.length" class="text-muted mb-3">No contract on this file yet.</div>
      <div v-for="item in contracts" :key="item.id" class="border rounded p-3 mb-3">
        <div class="d-flex justify-content-between gap-2 flex-wrap">
          <div>
            <strong>{{ item.original_name || item.label }}</strong>
            <span class="badge ms-2" :class="badgeClass(item.status)">{{ item.status_label }}</span>
          </div>
          <div class="d-flex gap-2 flex-wrap">
            <button v-if="item.has_file" class="btn btn-sm btn-outline-secondary" type="button" @click="openPart(item, 'file', item.original_name)">
              View contract
            </button>
            <button v-if="item.has_acceptance" class="btn btn-sm btn-outline-secondary" type="button" @click="openPart(item, 'acceptance', 'contract-acceptance.pdf')">
              View acceptance
            </button>
            <button v-if="mode === 'hr' && item.status !== 'signed'" class="btn btn-sm btn-outline-danger" type="button" @click="removeItem(item)">
              Remove
            </button>
          </div>
        </div>
        <div v-if="item.status === 'waiting' && mode === 'hr'" class="mt-2 small text-muted">
          The employee signs this from their dashboard after they set a password.
        </div>
        <div v-if="item.status === 'signed'" class="mt-2 small">
          Signed by {{ item.signer_name }} on {{ formatWhen(item.signed_at) }}.
          <span v-if="item.company_signatory_name">
            Company signatory: {{ item.company_signatory_name }}
            <span v-if="item.company_signatory_title">({{ item.company_signatory_title }})</span>.
          </span>
        </div>
        <div v-if="item.status === 'signed'" class="d-flex gap-4 flex-wrap mt-2">
          <div v-if="item.has_signature">
            <div class="text-muted small">Employee signature</div>
            <img v-if="previews[previewKey(item, 'signature')]" :src="previews[previewKey(item, 'signature')]" alt="Employee signature" class="signature-preview" />
          </div>
          <div v-if="item.has_company_signatory">
            <div class="text-muted small">Company signatory</div>
            <img v-if="previews[previewKey(item, 'signatory')]" :src="previews[previewKey(item, 'signatory')]" alt="Company signatory" class="signature-preview" />
          </div>
        </div>

        <div v-if="mode === 'self' && item.status === 'waiting'" class="mt-3">
          <label class="form-label">Your full name</label>
          <input v-model="signers[item.id]" class="form-control mb-2" type="text" maxlength="255" />
          <label class="form-label">Signature</label>
          <canvas
            :ref="(el) => setCanvas(item.id, el)"
            class="signature-pad"
            width="520"
            height="160"
            @pointerdown="startDraw(item.id, $event)"
            @pointermove="draw(item.id, $event)"
            @pointerup="stopDraw(item.id)"
            @pointerleave="stopDraw(item.id)"
          ></canvas>
          <div class="d-flex gap-2 mt-2">
            <button class="btn btn-outline-secondary btn-sm" type="button" @click="clearPad(item.id)">Clear</button>
            <button class="btn btn-primary btn-sm" type="button" :disabled="saving" @click="signContract(item)">
              Sign contract
            </button>
          </div>
        </div>
      </div>

      <h6 class="mb-2">Supporting documents</h6>
      <div v-if="!supportingDocs.length" class="text-muted">No supporting documents yet.</div>
      <div v-for="item in supportingDocs" :key="item.id" class="border rounded p-3 mb-2">
        <div class="d-flex justify-content-between gap-2 flex-wrap">
          <div>
            <strong>{{ item.label }}</strong>
            <span class="badge ms-2" :class="badgeClass(item.status)">{{ item.status_label }}</span>
            <div v-if="item.original_name" class="small text-muted">{{ item.original_name }}</div>
          </div>
          <div class="d-flex gap-2 flex-wrap">
            <button v-if="item.has_file" class="btn btn-sm btn-outline-secondary" type="button" @click="openPart(item, 'file', item.original_name)">
              Open
            </button>
            <button v-if="mode === 'hr' && item.status !== 'signed'" class="btn btn-sm btn-outline-danger" type="button" @click="removeItem(item)">
              Remove
            </button>
          </div>
        </div>
        <div v-if="mode === 'self' && item.status === 'requested'" class="mt-2">
          <input class="form-control" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" @change="onOwnFile(item.id, $event)" />
          <button class="btn btn-primary btn-sm mt-2" type="button" :disabled="saving || !ownFiles[item.id]" @click="uploadOwn(item)">
            Upload
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import employeeDocumentService from "@/services/hrm/employeeDocumentService";
import { toast } from "@/utils/toast";

const props = defineProps({
  mode: { type: String, default: "hr" },
  employeeId: { type: [Number, String], default: null },
});

const supportingTypes = [
  { value: "national_id", label: "National ID" },
  { value: "kra_pin", label: "KRA PIN certificate" },
  { value: "nssf", label: "NSSF" },
  { value: "shif", label: "SHIF" },
  { value: "certificate", label: "Certificate" },
  { value: "other", label: "Other" },
];

const loading = ref(false);
const saving = ref(false);
const rows = ref([]);
const summary = ref(null);
const contractFile = ref(null);
const supportingFile = ref(null);
const contractInput = ref(null);
const supportingInput = ref(null);
const supporting = reactive({ document_type: "national_id", title: "" });
const ownFiles = reactive({});
const signers = reactive({});
const previews = reactive({});
const pads = new Map();
const drawing = new Map();
const objectUrls = [];

const contracts = computed(() => rows.value.filter((row) => row.kind === "contract"));
const supportingDocs = computed(() => rows.value.filter((row) => row.kind !== "contract"));

const visibleChecklist = computed(() => {
  const list = summary.value?.checklist || [];
  if (props.mode === "hr") {
    return list;
  }
  return list.filter((item) => item.key === "contract_signed" || item.key === "documents");
});

function badgeClass(status) {
  if (status === "signed" || status === "received") return "bg-success";
  if (status === "waiting") return "bg-info";
  return "bg-warning text-dark";
}

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function previewKey(item, part) {
  return `${item.id}-${part}`;
}

function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (data?.errors) {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first) && first[0]) return first[0];
  }
  return data?.message || fallback;
}

async function load() {
  if (props.mode === "hr" && !props.employeeId) return;
  loading.value = true;
  try {
    const response = props.mode === "self"
      ? await employeeDocumentService.mine()
      : await employeeDocumentService.listForEmployee(props.employeeId);
    rows.value = response.data?.data || [];
    summary.value = response.data?.onboarding_summary || null;
    await loadPreviews();
  } catch (error) {
    toast(errorMessage(error, "Could not load documents."), "error");
  } finally {
    loading.value = false;
  }
}

async function loadPreviews() {
  Object.keys(previews).forEach((key) => delete previews[key]);
  for (const item of rows.value) {
    if (item.has_signature) await fetchPreview(item, "signature");
    if (item.has_company_signatory) await fetchPreview(item, "signatory");
  }
}

async function fetchPreview(item, part) {
  try {
    const url = props.mode === "self"
      ? employeeDocumentService.mineFileUrl(item.id, part)
      : employeeDocumentService.employeeFileUrl(props.employeeId, item.id, part);
    const response = await employeeDocumentService.download(url);
    const objectUrl = URL.createObjectURL(response.data);
    objectUrls.push(objectUrl);
    previews[previewKey(item, part)] = objectUrl;
  } catch {
    previews[previewKey(item, part)] = "";
  }
}

async function openPart(item, part, filename) {
  const url = props.mode === "self"
    ? employeeDocumentService.mineFileUrl(item.id, part)
    : employeeDocumentService.employeeFileUrl(props.employeeId, item.id, part);
  try {
    const response = await employeeDocumentService.download(url);
    const objectUrl = URL.createObjectURL(response.data);
    objectUrls.push(objectUrl);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.target = "_blank";
    if (filename) link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    toast(errorMessage(error, "Could not open that file."), "error");
  }
}

function onContractPicked(event) {
  contractFile.value = event.target.files?.[0] || null;
}

function onSupportingPicked(event) {
  supportingFile.value = event.target.files?.[0] || null;
}

function onOwnFile(id, event) {
  ownFiles[id] = event.target.files?.[0] || null;
}

async function uploadContract() {
  if (!contractFile.value) return;
  const form = new FormData();
  form.append("document_type", "contract");
  form.append("file", contractFile.value);
  saving.value = true;
  try {
    await employeeDocumentService.createForEmployee(props.employeeId, form);
    contractFile.value = null;
    if (contractInput.value) contractInput.value.value = "";
    toast("Contract uploaded. The employee can sign it after they sign in.");
    await load();
  } catch (error) {
    toast(errorMessage(error, "Could not upload the contract."), "error");
  } finally {
    saving.value = false;
  }
}

async function saveSupporting(withFile) {
  if (withFile && !supportingFile.value) return;
  if (supporting.document_type === "other" && !supporting.title.trim()) {
    toast("Give this document a name.", "error");
    return;
  }
  const form = new FormData();
  form.append("document_type", supporting.document_type);
  if (supporting.title.trim()) form.append("title", supporting.title.trim());
  if (withFile && supportingFile.value) form.append("file", supportingFile.value);
  saving.value = true;
  try {
    await employeeDocumentService.createForEmployee(props.employeeId, form);
    supportingFile.value = null;
    supporting.title = "";
    if (supportingInput.value) supportingInput.value.value = "";
    toast(withFile ? "Document saved on the employee file." : "The employee has been asked to upload this.");
    await load();
  } catch (error) {
    toast(errorMessage(error, "Could not save that document."), "error");
  } finally {
    saving.value = false;
  }
}

async function uploadOwn(item) {
  const file = ownFiles[item.id];
  if (!file) return;
  const form = new FormData();
  form.append("file", file);
  saving.value = true;
  try {
    await employeeDocumentService.uploadMine(item.id, form);
    ownFiles[item.id] = null;
    toast("Document uploaded.");
    await load();
  } catch (error) {
    toast(errorMessage(error, "Could not upload that file."), "error");
  } finally {
    saving.value = false;
  }
}

async function removeItem(item) {
  if (!window.confirm(`Remove ${item.label}?`)) return;
  saving.value = true;
  try {
    await employeeDocumentService.removeForEmployee(props.employeeId, item.id);
    toast("Removed.");
    await load();
  } catch (error) {
    toast(errorMessage(error, "Could not remove that document."), "error");
  } finally {
    saving.value = false;
  }
}

function setCanvas(id, el) {
  if (!el) return;
  pads.set(id, el);
  const ctx = el.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, el.width, el.height);
  ctx.strokeStyle = "#111827";
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
}

function point(canvas, event) {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.width,
    y: ((event.clientY - rect.top) / rect.height) * canvas.height,
  };
}

function startDraw(id, event) {
  const canvas = pads.get(id);
  if (!canvas) return;
  drawing.set(id, true);
  canvas.setPointerCapture?.(event.pointerId);
  const ctx = canvas.getContext("2d");
  const { x, y } = point(canvas, event);
  ctx.beginPath();
  ctx.moveTo(x, y);
}

function draw(id, event) {
  if (!drawing.get(id)) return;
  const canvas = pads.get(id);
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const { x, y } = point(canvas, event);
  ctx.lineTo(x, y);
  ctx.stroke();
}

function stopDraw(id) {
  drawing.set(id, false);
}

function clearPad(id) {
  const canvas = pads.get(id);
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
}

function padHasInk(canvas) {
  const ctx = canvas.getContext("2d");
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 0; i < data.length; i += 16) {
    if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) return true;
  }
  return false;
}

async function signContract(item) {
  const canvas = pads.get(item.id);
  const name = (signers[item.id] || "").trim();
  if (!name) {
    toast("Enter your full name.", "error");
    return;
  }
  if (!canvas || !padHasInk(canvas)) {
    toast("Draw your signature first.", "error");
    return;
  }
  saving.value = true;
  try {
    await employeeDocumentService.signMine(item.id, {
      signer_name: name,
      signature: canvas.toDataURL("image/png"),
    });
    toast("Contract signed.");
    await load();
  } catch (error) {
    toast(errorMessage(error, "Could not sign the contract."), "error");
  } finally {
    saving.value = false;
  }
}

watch(() => props.employeeId, () => {
  if (props.mode === "hr") load();
});

onMounted(load);

onBeforeUnmount(() => {
  objectUrls.forEach((url) => URL.revokeObjectURL(url));
});
</script>

<style scoped>
.signature-pad {
  width: 100%;
  max-width: 520px;
  height: 160px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  touch-action: none;
  cursor: crosshair;
}
.signature-preview {
  max-height: 72px;
  max-width: 220px;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 6px;
}
.dq-onboarding-checklist li + li {
  border-top: 1px dashed #e5e7eb;
}
</style>
